/**
 * OIDC Authorization Code + PKCE client.
 *
 * Zero external deps — just browser crypto + fetch. The viewer is a
 * *public* SPA client, so we use PKCE (RFC 7636) with S256 and never
 * see a client secret.
 *
 * Config via viewer-config.json (served by the Bun server) or
 * VITE_* env at build time.
 */

import type { AuthState, OidcClaims } from "./types";
import { extractRoles } from "./types";

export interface OidcConfig {
  issuer:     string;   // e.g. https://auth.vps.medpod.io
  clientId:   string;   // e.g. 253102312013592577@vaultpod
  redirectUri:string;   // e.g. https://vps.medpod.io/callback
  scopes:     string;   // default "openid profile email offline_access urn:zitadel:iam:org:project:roles"
}

const STORAGE_KEY = "vpd.auth.v1";
const PKCE_KEY    = "vpd.auth.pkce.v1";

let cachedCfg: OidcConfig | null = null;

export async function loadConfig(): Promise<OidcConfig> {
  if (cachedCfg) return cachedCfg;
  const rsp = await fetch("/viewer-config.json", { cache: "no-store" });
  if (!rsp.ok) throw new Error(`viewer-config.json: HTTP ${rsp.status}`);
  const raw = await rsp.json();
  cachedCfg = {
    issuer:      raw.issuer,
    clientId:    raw.clientId,
    redirectUri: raw.redirectUri ?? `${window.location.origin}/callback`,
    // Zitadel scope to request the role claim. Singular project:roles
    // (NOT plural projects:roles) — the plural form is silently ignored,
    // which causes the role claim to be omitted from the JWT and leaves
    // auth.roles empty in the SPA. Verified against Zitadel v2.65.1
    // on 2026-04-26.
    scopes:      raw.scopes ?? "openid profile email offline_access urn:zitadel:iam:org:project:roles",
  };
  return cachedCfg;
}

// ------------------ PKCE helpers ------------------

function randomString(n: number): string {
  const bytes = new Uint8Array(n);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

function base64url(bytes: Uint8Array): string {
  let s = btoa(String.fromCharCode(...bytes));
  return s.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function sha256b64url(s: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return base64url(new Uint8Array(digest));
}

// ------------------ Login flow ------------------

export async function startLogin(): Promise<void> {
  const cfg = await loadConfig();
  const verifier = randomString(64);
  const state = randomString(24);
  const challenge = await sha256b64url(verifier);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: "code",
    scope: cfg.scopes,
    redirect_uri: cfg.redirectUri,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(`${cfg.issuer}/oauth/v2/authorize?${params}`);
}

export async function finishLogin(params: URLSearchParams): Promise<AuthState> {
  const cfg = await loadConfig();
  const raw = sessionStorage.getItem(PKCE_KEY);
  if (!raw) throw new Error("PKCE state missing — restart login");
  const { verifier, state } = JSON.parse(raw);
  sessionStorage.removeItem(PKCE_KEY);
  if (params.get("state") !== state) throw new Error("PKCE state mismatch");
  const code = params.get("code");
  if (!code) throw new Error(`authorize error: ${params.get("error") ?? "no code"}`);

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    code_verifier: verifier,
  });
  // Same-origin proxy (`/auth/token`) forwards to the issuer server-side.
  // Keeps the browser from making a cross-origin fetch that privacy-focused
  // browsers (Firefox ETP, add-ons) sometimes block with a generic
  // "NetworkError when attempting to fetch resource". See server.ts authProxy.
  const rsp = await fetch("/auth/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!rsp.ok) throw new Error(`token: HTTP ${rsp.status} — ${await rsp.text()}`);
  const tok = await rsp.json();
  return ingest(tok);
}

export async function refresh(state: AuthState): Promise<AuthState | null> {
  if (!state.refreshToken) return null;
  const cfg = await loadConfig();
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: state.refreshToken,
    client_id: cfg.clientId,
  });
  const rsp = await fetch("/auth/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!rsp.ok) return null;
  return ingest(await rsp.json());
}

function ingest(tok: any): AuthState {
  const idClaims = decodeJwt(tok.id_token) as OidcClaims;
  const state: AuthState = {
    idToken: tok.id_token,
    accessToken: tok.access_token,
    refreshToken: tok.refresh_token,
    claims: idClaims,
    roles: extractRoles(idClaims),
    expiresAt: Math.floor(Date.now() / 1000) + (tok.expires_in ?? 900),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

export function loadFromStorage(): AuthState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as AuthState;
    if (state.expiresAt && state.expiresAt * 1000 < Date.now() - 30_000) {
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

export function logout(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  loadConfig().then((cfg) => {
    // Zitadel requires an *exact* string match with one of the
    // app's configured postLogoutRedirectUris. We send `/login` so
    // the user lands on our own sign-in page, and the OIDC app
    // must have that URI in its allow-list — otherwise Zitadel
    // drops the user on its own self-service profile page.
    const p = new URLSearchParams({
      post_logout_redirect_uri: `${window.location.origin}/login`,
      client_id: cfg.clientId,
    });
    window.location.assign(`${cfg.issuer}/oidc/v1/end_session?${p}`);
  });
}

/** JWT decode (no verify) — full signature verification happens on the
 *  backend. Client just needs the claim set for UI gating. */
function decodeJwt(jwt: string): any {
  const parts = jwt.split(".");
  if (parts.length !== 3) throw new Error("malformed jwt");
  const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, "=")));
}
